import CloseIcon from '@mui/icons-material/Close';
import type { AlertColor } from '@mui/material/Alert';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import type { RequireAtLeastOneNotNull } from '@openx/types/helpers.ts';
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type InfoTileItem = RequireAtLeastOneNotNull<{
  title?: ReactNode;
  body?: ReactNode;
}>;

export type InfoTileSecondaryAction = {
  onAction: () => void;
  button: ReactNode | string;
};

export type InfoTileProps = {
  severity?: AlertColor;
  item: InfoTileItem | string;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
  'data-test'?: string;
  closeable?: boolean;
  onClose?: () => void;
  disabled?: boolean;
  secondaryAction?: InfoTileSecondaryAction;
};

/**
 * A flexible information display tile built on MUI Alert.
 *
 * Supports optional close functionality and secondary actions.
 *
 * @example
 * // Basic usage
 * <InfoTile
 *   severity="info"
 *   item={{ title: "Tip", body: "This is helpful information" }}
 * />
 *
 * @example
 * // With close button
 * <InfoTile
 *   severity="info"
 *   item={{ title: "Dismissible", body: "Click X to close" }}
 *   closeable
 *   onClose={() => console.log('Closed!')}
 * />
 *
 * @example
 * // With secondary action
 * <InfoTile
 *   severity="warning"
 *   item={{ body: "Something to note" }}
 *   secondaryAction={{
 *     onAction: () => console.log('Learn more clicked'),
 *     button: "Learn More"
 *   }}
 * />
 *
 * @example
 * // With string item
 * <InfoTile item="Yay! Everything is working smoothly." />
 */
export function InfoTile({
  severity = 'info',
  item,
  icon,
  sx = [],
  closeable,
  onClose,
  disabled,
  secondaryAction,
  ...props
}: InfoTileProps): ReactNode {
  const { t } = useTranslation();
  const [isHidden, setIsHidden] = useState(false);

  const dataTest = props['data-test'] ?? 'info-tile';
  const { title, body } = typeof item === 'string' ? { body: item } : item;
  const showCloseButton = closeable === true || (onClose !== undefined && closeable !== false);

  const handleClose = useCallback(() => {
    onClose?.();
    setIsHidden(true);
  }, [onClose]);

  if (isHidden) {
    return null;
  }

  const renderSecondaryActionButton = () => {
    if (!secondaryAction) return null;

    const { onAction, button } = secondaryAction;

    return (
      <Button
        size="small"
        color="primary"
        onClick={onAction}
        disabled={disabled}
        data-test={`${dataTest}-secondary-action`}
      >
        {button}
      </Button>
    );
  };

  const renderCloseButton = () => {
    if (!showCloseButton) return null;
    return (
      <IconButton
        size="small"
        onClick={handleClose}
        disabled={disabled}
        data-test={`${dataTest}-close`}
        aria-label={t('Close')}
        color="primary"
      >
        <CloseIcon data-test="close-icon" />
      </IconButton>
    );
  };

  const renderAction = () => {
    const secondary = renderSecondaryActionButton();
    const close = renderCloseButton();

    if (!secondary && !close) return undefined;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, ml: 'auto' }}>
        {secondary}
        {close}
      </Box>
    );
  };

  return (
    <Alert
      severity={severity}
      icon={icon}
      data-test={dataTest}
      sx={[
        {
          mt: 2,
          '& .MuiAlert-message': {
            flex: 1,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {title && (
        <AlertTitle data-test={`${dataTest}-title`} sx={{ mb: body ? 0.5 : 0, display: 'flex' }}>
          {title} {renderAction()}
        </AlertTitle>
      )}
      {body && (
        <Box data-test={`${dataTest}-body`} component="div">
          {body}
        </Box>
      )}
    </Alert>
  );
}
